import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuAction,
} from "@components/ui/sidebar";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ChevronRightIcon,
  MoreHorizontalIcon,
  TagIcon,
  TagsIcon,
  Trash2Icon,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import { Link } from "react-router-dom";
import { ROUTES } from "@/routes_new";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@components/ui/collapsible";
import { deleteTag, getTags } from "@/api/endpoints/tags";
import { toast } from "sonner";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { useMemo, useState } from "react";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@components/ui/empty";
import { Spinner } from "@components/ui/spinner";
import Error from "@components/ui/error";
import AccessControl from "@components/AccessControl";
import { Tag } from "@/types/Tag";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@components/ui/alert-dialog";

export function TagsNav({
  orgUuid,
  pathname,
}: {
  orgUuid: string;
  pathname: string;
}) {
  const { t } = useLingui();
  const [isTagsOpen, setIsTagsOpen] = useLocalStorage(
    "sidebar-tags-open",
    false
  );

  const {
    data: tags,
    refetch: refetchTags,
    isLoading: isTagsLoading,
    error: tagsError,
  } = useQuery({
    queryKey: [orgUuid, "tags"],
    queryFn: () => getTags(),
    refetchOnWindowFocus: false,
  });

  const { customTags, hasTags } = useMemo(() => {
    if (tags?.ok) {
      const customTags = tags.data.filter((tag) => tag.name !== "favorited");
      const hasTags = customTags.length > 0;

      return { customTags, hasTags };
    }

    return { customTags: [], hasTags: false };
  }, [tags]);

  const [deletingTag, setDeletingTag] = useState<Tag["slug"]>();

  const { mutate: mutateDeleteTag } = useMutation({
    mutationFn: deleteTag,
    onSuccess: (res) => {
      if (res.ok) {
        toast.success(t`Taggen har tagits bort`);
        refetchTags();
      } else {
        toast.error(t`Kunde inte ta bort taggen`);
      }
    },
    onError: () => {
      toast.error(t`Kunde inte ta bort taggen`);
    },
  });

  function handleDeleteTag() {
    if (!deletingTag) {
      return;
    }

    mutateDeleteTag(deletingTag);

    setDeletingTag(undefined);
  }

  return (
    <>
      <Collapsible
        open={isTagsOpen}
        onOpenChange={setIsTagsOpen}
        className="group/tags"
      >
        <SidebarGroup>
          <SidebarGroupLabel asChild>
            <CollapsibleTrigger data-testid="tags-section">
              <Trans>Taggar</Trans>
              <ChevronRightIcon className="ml-auto transition-transform group-data-[state=open]/tags:rotate-90" />
            </CollapsibleTrigger>
          </SidebarGroupLabel>
          <CollapsibleContent>
            {tags?.ok ? (
              hasTags ? (
                <SidebarGroupContent>
                  <SidebarMenu>
                    {customTags.map((tag) => {
                      const to = ROUTES.TAG_ORGID.replace(
                        ":orgUuid",
                        orgUuid
                      ).replace(":slug", tag.slug);

                      return (
                        <SidebarMenuItem key={tag.slug}>
                          <SidebarMenuButton
                            asChild
                            isActive={pathname === to}
                            data-testid="tags-item"
                          >
                            <Link to={to}>
                              <TagIcon />
                              {tag.name}
                            </Link>
                          </SidebarMenuButton>

                          <AccessControl>
                            <DropdownMenu modal={false}>
                              <DropdownMenuTrigger asChild>
                                <SidebarMenuAction showOnHover>
                                  <MoreHorizontalIcon />
                                </SidebarMenuAction>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent>
                                <DropdownMenuItem
                                  variant="destructive"
                                  onSelect={() => {
                                    setDeletingTag(tag.slug);
                                  }}
                                  data-testid="delete-tag-button"
                                >
                                  <Trash2Icon />
                                  <Trans>Ta bort tagg</Trans>
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </AccessControl>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              ) : (
                <Empty className="py-4 md:py-6">
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <TagsIcon />
                    </EmptyMedia>
                    <EmptyTitle>
                      <Trans>Inga taggar skapade</Trans>
                    </EmptyTitle>
                    <EmptyDescription>
                      <Trans>Du har inte skapat några taggar än.</Trans>
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              )
            ) : isTagsLoading ? (
              <Spinner className="mx-auto" />
            ) : (
              <Error
                className="py-4 md:py-6"
                error={tagsError}
                heading={t`Åh nej!`}
                description={t`Något gick snett med att ladda taggarna. Vänligen försök igen senare`}
              />
            )}
          </CollapsibleContent>
        </SidebarGroup>
      </Collapsible>

      <AlertDialog
        open={!!deletingTag}
        onOpenChange={() => {
          setDeletingTag(undefined);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Ta bort tagg</Trans>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>Är du säker på att du vill ta bort den här taggen?</Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <Trans>Avbryt</Trans>
            </AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleDeleteTag}>
              <Trans>Ta bort</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
